/*
 *   File : analytics.js
 *   Author : https://evoqins.com
 *   Description : Dual-dispatch analytics helper — fires every event to GA4
 *                 (via the gtag loaded in public/index.html), AppsFlyer PBA
 *                 (People-Based Attribution) Web SDK via the global AF(), and
 *                 Firebase Analytics (for cross-platform GA4 reporting with
 *                 the mobile app). All sinks fail silently / no-op so
 *                 analytics can never break the UX, and AppsFlyer stays
 *                 dormant until the team pastes the PBA snippet into
 *                 index.html (see the commented block there).
 *
 *                 NOTE: The account only has "PBA - Legacy" enabled for browser
 *                 web. If/when the modern AppsFlyer "Web SDK (Beta)" is enabled,
 *                 only the AppsFlyer dispatch in `_logAf` needs updating.
 *   Version : 1.1.0
*/

import { logEvent } from "firebase/analytics";
import { analytics } from "../Container/Auth/firebase";

const PRODUCTION_HOSTNAME = "app.indexfundssahihai.com";

const _isProductionAnalytics = () => {
    try {
        return typeof window !== "undefined" && window.location.hostname === PRODUCTION_HOSTNAME;
    } catch (e) {
        return false;
    }
};

// Fire an event to GA4 via gtag. Production only (app.indexfundssahihai.com).
// gtag.js buffers into dataLayer before it finishes loading, so no readiness
// check is needed — only a guard for ad-blockers / non-production environments.
const _gtagEvent = (name, params) => {
    try {
        if (!_isProductionAnalytics()) return;
        if (typeof window.gtag === "function") {
            window.gtag("event", name, params);
        }
    } catch (e) {
        // swallow — analytics must never break the app
    }
};

// Fire an event to AppsFlyer PBA via the global AF(). The PBA snippet defines a
// queueing stub on window.AF immediately, so calls made before the async script
// finishes are buffered. We only guard for existence (snippet not added / blocked).
const _logAf = (name, params) => {
    try {
        if (typeof window !== "undefined" && typeof window.AF === "function") {
            window.AF("pba", "event", {
                eventType: "EVENT",
                eventName: name,
                eventValue: params,
            });
        }
    } catch (e) {
        // swallow
    }
};

// Fire an event to Firebase Analytics. Production only, same as gtag — this
// feeds a single hardcoded measurement stream, not one that should see
// dev/staging noise. `analytics` resolves asynchronously (see firebase.js)
// so early events before it's ready are dropped, same tradeoff as `_gtagEvent`.
const _logFirebase = (name, params) => {
    try {
        if (!_isProductionAnalytics()) return;
        if (analytics) {
            logEvent(analytics, name, params);
        }
    } catch (e) {
        // swallow
    }
};

/**
 * Track an analytics event. Dispatches to GA4, AppsFlyer PBA, and Firebase Analytics.
 * @param {string} name   lowercase_snake event name (<=40 chars for GA4)
 * @param {object} params flat params object (no nested objects; no PII)
 */
const _trackEvent = (name, params = {}) => {
    if (!name) return;
    _gtagEvent(name, params);
    _logAf(name, params);
    _logFirebase(name, params);
};

export { _trackEvent };
